import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  IconButton,
  Divider,
  FormControlLabel,
  Switch,
  Slider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomButton from "../../Button/CustomButton";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";

const AssistantModal = ({ open, onClose, data, onSave }) => {
  const isEdit = !!data;

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    language: "English",
    voice: "Rachel - Professional",
    personality: "Professional",
    model: "GPT-4",
    temperature: 0.7,
    maxTokens: 2000,
    systemPrompt: "",
    greetingMessage: "",
    fallbackMessage: "",
    tools: [],
    phoneNumbers: [],
    knowledgeBase: [],
    transferEnabled: false,
    recordingEnabled: true,
    sentimentAnalysis: true,
    status: "Active",
  });

  const [newTool, setNewTool] = useState("");
  const [newPhoneNumber, setNewPhoneNumber] = useState("");

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || "",
        description: data.description || "",
        language: data.language || "English",
        voice: data.voice || "Rachel - Professional",
        personality: data.personality || "Professional",
        model: data.model || "GPT-4",
        temperature: data.temperature || 0.7,
        maxTokens: data.maxTokens || 2000,
        systemPrompt: data.systemPrompt || "",
        greetingMessage: data.greetingMessage || "",
        fallbackMessage: data.fallbackMessage || "",
        tools: data.tools || [],
        phoneNumbers: data.phoneNumbers || [],
        knowledgeBase: data.knowledgeBase || [],
        transferEnabled: data.transferEnabled || false,
        recordingEnabled: data.recordingEnabled !== false,
        sentimentAnalysis: data.sentimentAnalysis !== false,
        status: data.status || "Active",
      });
    }
  }, [data]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleAddTool = () => {
    if (newTool.trim()) {
      setFormData({
        ...formData,
        tools: [...formData.tools, newTool.trim()],
      });
      setNewTool("");
    }
  };

  const handleRemoveTool = (tool) => {
    setFormData({
      ...formData,
      tools: formData.tools.filter((t) => t !== tool),
    });
  };

  const handleAddPhoneNumber = () => {
    if (newPhoneNumber.trim()) {
      setFormData({
        ...formData,
        phoneNumbers: [...formData.phoneNumbers, newPhoneNumber.trim()],
      });
      setNewPhoneNumber("");
    }
  };

  const handleRemovePhoneNumber = (number) => {
    setFormData({
      ...formData,
      phoneNumbers: formData.phoneNumbers.filter((n) => n !== number),
    });
  };

  const handleSubmit = () => {
    onSave(formData);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      name: "",
      description: "",
      language: "English",
      voice: "Rachel - Professional",
      personality: "Professional",
      model: "GPT-4",
      temperature: 0.7,
      maxTokens: 2000,
      systemPrompt: "",
      greetingMessage: "",
      fallbackMessage: "",
      tools: [],
      phoneNumbers: [],
      knowledgeBase: [],
      transferEnabled: false,
      recordingEnabled: true,
      sentimentAnalysis: true,
      status: "Active",
    });
    setNewTool("");
    setNewPhoneNumber("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h5" fontWeight={600}>
            {isEdit ? "Edit Assistant" : "Create New Assistant"}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Basic Information */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Basic Information
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label="Assistant Name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                required
                placeholder="e.g., Customer Support Agent"
              />
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                multiline
                rows={2}
                placeholder="Brief description of the assistant's purpose"
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={formData.language}
                    label="Language"
                    onChange={(e) => handleChange("language", e.target.value)}
                  >
                    <MenuItem value="English">English</MenuItem>
                    <MenuItem value="Spanish">Spanish</MenuItem>
                    <MenuItem value="French">French</MenuItem>
                    <MenuItem value="German">German</MenuItem>
                    <MenuItem value="Italian">Italian</MenuItem>
                    <MenuItem value="Portuguese">Portuguese</MenuItem>
                    <MenuItem value="Japanese">Japanese</MenuItem>
                    <MenuItem value="Chinese">Chinese</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => handleChange("status", e.target.value)}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                    <MenuItem value="Testing">Testing</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Voice & AI Configuration */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Voice & AI Configuration
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Voice</InputLabel>
                <Select
                  value={formData.voice}
                  label="Voice"
                  onChange={(e) => handleChange("voice", e.target.value)}
                >
                  <MenuItem value="Rachel - Professional">Rachel - Professional</MenuItem>
                  <MenuItem value="Matthew - Friendly">Matthew - Friendly</MenuItem>
                  <MenuItem value="Sophia - Warm">Sophia - Warm</MenuItem>
                  <MenuItem value="James - Authoritative">James - Authoritative</MenuItem>
                  <MenuItem value="Emma - Energetic">Emma - Energetic</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>AI Model</InputLabel>
                  <Select
                    value={formData.model}
                    label="AI Model"
                    onChange={(e) => handleChange("model", e.target.value)}
                  >
                    <MenuItem value="GPT-4">GPT-4 (Most Capable)</MenuItem>
                    <MenuItem value="GPT-3.5-Turbo">GPT-3.5 Turbo (Fast)</MenuItem>
                    <MenuItem value="Claude-3">Claude 3 (Advanced)</MenuItem>
                    <MenuItem value="PaLM-2">PaLM 2 (Google)</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Personality</InputLabel>
                  <Select
                    value={formData.personality}
                    label="Personality"
                    onChange={(e) => handleChange("personality", e.target.value)}
                  >
                    <MenuItem value="Professional">Professional</MenuItem>
                    <MenuItem value="Friendly">Friendly</MenuItem>
                    <MenuItem value="Empathetic">Empathetic</MenuItem>
                    <MenuItem value="Authoritative">Authoritative</MenuItem>
                    <MenuItem value="Casual">Casual</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom fontWeight={600}>
                  Temperature: {formData.temperature.toFixed(2)}
                </Typography>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Lower = more focused and deterministic, Higher = more creative and varied
                </Typography>
                <Slider
                  value={formData.temperature}
                  onChange={(e, value) => handleChange("temperature", value)}
                  min={0}
                  max={1}
                  step={0.1}
                  valueLabelDisplay="auto"
                  marks={[
                    { value: 0, label: "0" },
                    { value: 0.5, label: "0.5" },
                    { value: 1, label: "1" },
                  ]}
                />
              </Box>

              <TextField
                fullWidth
                label="Max Tokens"
                type="number"
                value={formData.maxTokens}
                onChange={(e) => handleChange("maxTokens", parseInt(e.target.value))}
                InputProps={{ inputProps: { min: 100, max: 8000 } }}
              />
            </Box>
          </Box>

          <Divider />

          {/* Prompts & Messages */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Prompts & Messages
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label="System Prompt"
                value={formData.systemPrompt}
                onChange={(e) => handleChange("systemPrompt", e.target.value)}
                multiline
                rows={3}
                placeholder="You are a helpful customer support assistant..."
              />
              <TextField
                fullWidth
                label="Greeting Message"
                value={formData.greetingMessage}
                onChange={(e) => handleChange("greetingMessage", e.target.value)}
                multiline
                rows={2}
                placeholder="Hello! How can I help you today?"
              />
              <TextField
                fullWidth
                label="Fallback Message"
                value={formData.fallbackMessage}
                onChange={(e) => handleChange("fallbackMessage", e.target.value)}
                multiline
                rows={2}
                placeholder="I'm sorry, I didn't understand that. Could you please rephrase?"
              />
            </Box>
          </Box>

          <Divider />

          {/* Tools Integration */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Tools & Integrations
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Add Tool"
                value={newTool}
                onChange={(e) => setNewTool(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleAddTool();
                  }
                }}
                placeholder="e.g., Calendar API, CRM Lookup"
              />
              <CustomButton
                icon={<AddIcon />}
                iconPosition="left"
                text="Add"
                onClick={handleAddTool}
                disabled={!newTool.trim()}
                size="small"
              />
            </Box>
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 2 }}>
              {formData.tools.map((tool, idx) => (
                <Chip
                  key={idx}
                  label={tool}
                  onDelete={() => handleRemoveTool(tool)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>

          <Divider />

          {/* Phone Numbers */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Phone Numbers
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Add Phone Number"
                value={newPhoneNumber}
                onChange={(e) => setNewPhoneNumber(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleAddPhoneNumber();
                  }
                }}
                placeholder="e.g., +1 (555) 123-4567"
              />
              <CustomButton
                icon={<AddIcon />}
                iconPosition="left"
                text="Add"
                onClick={handleAddPhoneNumber}
                disabled={!newPhoneNumber.trim()}
                size="small"
              />
            </Box>
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 2 }}>
              {formData.phoneNumbers.map((number, idx) => (
                <Chip
                  key={idx}
                  label={number}
                  onDelete={() => handleRemovePhoneNumber(number)}
                  color="secondary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>

          <Divider />

          {/* Features */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Features
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.recordingEnabled}
                    onChange={(e) => handleChange("recordingEnabled", e.target.checked)}
                    color="primary"
                  />
                }
                label="Enable Call Recording"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.sentimentAnalysis}
                    onChange={(e) => handleChange("sentimentAnalysis", e.target.checked)}
                    color="primary"
                  />
                }
                label="Enable Sentiment Analysis"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.transferEnabled}
                    onChange={(e) => handleChange("transferEnabled", e.target.checked)}
                    color="primary"
                  />
                }
                label="Enable Call Transfer"
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <CustomButton text="Cancel" variant="outlined" onClick={handleClose} />
        <CustomButton
          icon={<SaveIcon />}
          iconPosition="left"
          text={isEdit ? "Update Assistant" : "Create Assistant"}
          onClick={handleSubmit}
          disabled={!formData.name.trim()}
        />
      </DialogActions>
    </Dialog>
  );
};

export default AssistantModal;
